import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  redirect,
  useRouteContext,
} from '@tanstack/react-router';
import { SearchLg } from '@untitledui/icons/SearchLg';
import { Users01 } from '@untitledui/icons/Users01';
import { AnimatePresence } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import {
  ParticipantSource,
  ProjectSortField,
  SortBy,
  type TeamRespondentDto,
} from '@/api/core-api/types.gen';
import { EmptyState } from '@/components/application/empty-state/empty-state';
import { PaginationPageDefault } from '@/components/application/pagination/pagination';
import { Table, TableCard } from '@/components/application/table/table';
import {
  Button,
  ExternalLinkButton,
  LinkButton,
} from '@/components/base/buttons/button';
import { Checkbox } from '@/components/base/checkbox/checkbox';
import { ResearcherShell } from '@/components/navigation/researcher-shell';
import {
  INVITE_TARGET_STATUSES,
  InviteParticipantsModal,
  toInviteProjectOption,
} from '@/components/participants/invite-participants-modal';
import { deriveTeamCapabilities } from '@/hooks/api/use-team-capabilities';
import {
  TEAM_PARTICIPANTS_PAGE_SIZE,
  teamParticipantsQueryOptions,
} from '@/hooks/api/use-team-participants';
import { teamProjectsQueryOptions } from '@/hooks/api/use-team-projects';
import { useDebounced } from '@/hooks/use-debounced';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { teamPlanSummaryQueryOptions } from '@/routes/researchers/_authenticated/projects/$projectId/-launch/team-pricing-version';
import { getPublicEnv } from '@/utils/session/get-public-env';
import { resolveActiveDashboard } from '@/utils/session/resolve-active-dashboard';

import { ParticipantDatabaseDetailPane } from './-components/participant-database-detail-pane';
import {
  ParticipantDatabaseRow,
  ParticipantDatabaseRowSkeleton,
} from './-components/participant-database-row';
import { ParticipantsToolbar } from './-components/participants-toolbar';
import { useCappedSelection } from './-use-capped-selection';

/**
 * Legacy parity: at most 50 participants can be invited in one batch. Selection
 * is page-scoped, so this is kept equal to `TEAM_PARTICIPANTS_PAGE_SIZE` — a
 * smaller page would put the cap out of reach.
 */
const MAX_SELECTION = TEAM_PARTICIPANTS_PAGE_SIZE;

/** The API ignores shorter name searches, so neither does the UI apply them. */
const MIN_SEARCH_LENGTH = 2;

/** Placeholder rows rendered while the first page of the roster loads. */
const SKELETON_ROWS = Array.from({ length: 8 }, (_, i) => `skeleton-${i}`);

/**
 * Invite targets are fetched in one page. Live projects per team are few, so
 * this is a ceiling rather than a window.
 */
const INVITE_TARGET_PAGE_SIZE = 200;

const participantsSearchSchema = z.object({
  page: z.number().int().positive().catch(1).default(1),
  // TanStack Router JSON-parses search params, so `?q=123` arrives as a
  // number — coerce so numeric queries don't crash schema validation.
  q: z.coerce.string().max(255).optional().catch(undefined),
  source: z.enum(ParticipantSource).optional(),
  sortBy: z.enum(SortBy).catch(SortBy.MOSTRECENT).default(SortBy.MOSTRECENT),
  projectIds: z.array(z.string()).catch([]).default([]),
  /** Profile id open in the detail pane. */
  selected: z.string().optional(),
});

export type ParticipantsSearch = z.infer<typeof participantsSearchSchema>;

const EMPTY_PARTICIPANTS: TeamRespondentDto[] = [];

export const Route = createFileRoute(
  '/researchers/_authenticated/participant-database/',
)({
  validateSearch: participantsSearchSchema,
  beforeLoad: async ({ context, location }) => {
    const resolved = await resolveActiveDashboard({ context, location });
    const organizationId = resolved.activeDashboard.organization ?? undefined;
    const teamId = resolved.activeDashboard.team ?? undefined;

    // The one hard redirect: with the new UI off there is no new-app participant
    // surface at all, so fall back to the legacy dashboard entirely.
    if (!resolved.v3Enabled) {
      const env = await getPublicEnv();
      if (env.WEBSITE_URL.length > 0 && organizationId && teamId) {
        throw redirect({
          href: `${env.WEBSITE_URL}/dashboard/organizations/${organizationId}/teams/${teamId}/participants`,
        });
      }
      return { ...resolved, hasParticipantDatabase: false };
    }

    // The v4 team-respondents endpoints enforce the capability off the active
    // plan. Resolve it up-front so the page can render an in-app upgrade prompt
    // for uncapable teams instead of bouncing them out to the legacy dashboard.
    let hasParticipantDatabase = false;
    if (teamId) {
      try {
        const summary = await context.queryClient.ensureQueryData(
          teamPlanSummaryQueryOptions(teamId, resolved.user.id),
        );
        hasParticipantDatabase =
          deriveTeamCapabilities(summary).hasParticipantDatabase;
      } catch {
        // The plan-summary endpoint 403s for legacy-pricing teams
        // (core-api's PricingGuard rejects `newPricing: false` teams), so a
        // thrown error here doesn't mean the page is broken — it means the
        // capability can't be established. Default it closed, matching
        // `useTeamCapabilities`, so those teams get the upgrade prompt
        // instead of the route-level error boundary.
      }
    }

    return { ...resolved, hasParticipantDatabase };
  },
  component: ParticipantDatabasePage,
  head: () => ({ meta: [{ title: t`Participant database` }] }),
});

function ParticipantDatabasePage() {
  const { user, activeDashboard, hasParticipantDatabase } =
    Route.useRouteContext();
  const { env } = useRouteContext({ from: '__root__' });
  const teamId = activeDashboard.team ?? '';
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const analytics = useAnalytics();

  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  useEffect(() => {
    analytics?.ui({
      action: 'viewed',
      actionSubject: 'participantDatabase',
      source: 'participantDatabase',
    });
  }, [analytics]);

  const updateSearch = useCallback(
    (updates: Partial<ParticipantsSearch>, opts?: { resetPage?: boolean }) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          ...updates,
          ...(opts?.resetPage === false ? {} : { page: 1 }),
        }),
        replace: true,
      });
    },
    [navigate],
  );

  // The query this page last pushed into the URL. It tells our own debounced
  // commits apart from external changes (back/forward, deep-link) so the
  // re-sync below never rewinds the box to an older value while the user is
  // still typing.
  const committedQuery = useRef(search.q);

  // Re-sync the draft when the URL query changes underneath us.
  useEffect(() => {
    if (search.q === committedQuery.current) return;
    committedQuery.current = search.q;
    setSearchInput(search.q ?? '');
  }, [search.q]);

  useDebounced(
    () => {
      const trimmed = searchInput.trim();
      // Below the minimum the API would ignore the term, so it never becomes an
      // active filter — keeping the URL, the filter state and the request in
      // agreement instead of showing unfiltered rows under an applied search.
      const next = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : undefined;
      if (next === committedQuery.current) return;
      committedQuery.current = next;
      updateSearch({ q: next });
    },
    300,
    [searchInput],
  );

  // Deep links can still carry a too-short `q`; ignore it everywhere at once.
  const activeQuery =
    search.q && search.q.length >= MIN_SEARCH_LENGTH ? search.q : undefined;

  const apiParams = useMemo(
    () => ({
      teamId,
      authUserId: user.id,
      page: search.page,
      name: activeQuery,
      source: search.source,
      sortBy: search.sortBy,
      projectIds: search.projectIds.length > 0 ? search.projectIds : undefined,
    }),
    [teamId, user.id, activeQuery, search],
  );

  // Skip the data queries entirely for uncapable teams — the v4 endpoints would
  // 403, and the page renders the upgrade prompt instead of the roster.
  const { data, isPending, isPlaceholderData, isError, refetch } = useQuery({
    ...teamParticipantsQueryOptions(apiParams),
    enabled: !!teamId && hasParticipantDatabase,
  });

  // Projects for the "filter by project" control, most recent first. Bounded to
  // a single page: a list long enough to need paging would need its own search
  // affordance, which this dropdown doesn't have.
  const { data: filterProjectsData } = useQuery({
    ...teamProjectsQueryOptions({
      teamId,
      authUserId: user.id,
      page: 1,
      sortBy: ProjectSortField.CREATED_AT,
      sortDir: 'desc',
    }),
    enabled: !!teamId && hasParticipantDatabase,
  });

  // Invite targets are filtered server-side instead of being sliced out of the
  // list above — an eligible project sitting past its first page would drop out
  // of the picker and wrongly disable "Invite to project".
  const { data: inviteProjectsData } = useQuery({
    ...teamProjectsQueryOptions({
      teamId,
      authUserId: user.id,
      page: 1,
      pageSize: INVITE_TARGET_PAGE_SIZE,
      statuses: INVITE_TARGET_STATUSES,
      closed: false,
      sortBy: ProjectSortField.CREATED_AT,
      sortDir: 'desc',
    }),
    enabled: !!teamId && hasParticipantDatabase,
  });

  const filterProjectOptions = useMemo(
    () =>
      (filterProjectsData?.results ?? []).map((p) => ({
        id: p.id,
        name: p.name,
      })),
    [filterProjectsData],
  );
  const inviteProjectOptions = useMemo(
    () =>
      (inviteProjectsData?.results ?? [])
        // `closed` is only applied server-side to PAID projects, so an approved
        // or partially-paid project that was closed can still come back here.
        .filter((p) => p.closed !== true)
        .map((p) => toInviteProjectOption(p)),
    [inviteProjectsData],
  );

  const participants = data?.results ?? EMPTY_PARTICIPANTS;
  const totalResults = data?.totalResults ?? 0;
  const totalPages = Math.max(
    1,
    Math.ceil(totalResults / TEAM_PARTICIPANTS_PAGE_SIZE),
  );

  // A stale deep link (`?page=99`) would otherwise render an empty table with
  // the pagination hidden, leaving no way back. Once the real totals are in,
  // pull the page back into range.
  useEffect(() => {
    if (isPending || isPlaceholderData) return;
    if (search.page > totalPages) {
      updateSearch({ page: totalPages }, { resetPage: false });
    }
  }, [isPending, isPlaceholderData, search.page, totalPages, updateSearch]);

  const visibleIds = useMemo(
    () => participants.map((p) => p.profileId),
    [participants],
  );

  // Selection is page-scoped: it resets whenever the active filters change so
  // it never leaks across pages or filters. Select-all honours the same
  // MAX_SELECTION cap as individual rows.
  const selectionResetKey = useMemo(
    () =>
      JSON.stringify([
        search.page,
        activeQuery,
        search.source,
        search.sortBy,
        search.projectIds,
      ]),
    [search.page, activeQuery, search.source, search.sortBy, search.projectIds],
  );
  const {
    selectedIds,
    canSelectMore,
    allVisibleSelected,
    someVisibleSelected,
    toggleSelected,
    toggleSelectAll,
    clear: clearSelection,
  } = useCappedSelection({
    visibleIds,
    max: MAX_SELECTION,
    resetKey: selectionResetKey,
  });

  const selectedParticipant = search.selected ?? null;
  const setSelected = useCallback(
    (profileId: string | null) => {
      updateSearch({ selected: profileId ?? undefined }, { resetPage: false });
    },
    [updateSearch],
  );

  const handlePageChange = useCallback(
    (page: number) => updateSearch({ page }, { resetPage: false }),
    [updateSearch],
  );

  const openInvite = useCallback(() => {
    analytics?.ui({
      action: 'clicked',
      actionSubject: 'reviewAndInvite',
      source: 'participantDatabase',
      attributes: { count: selectedIds.size },
    });
    setIsInviteOpen(true);
  }, [analytics, selectedIds.size]);

  const hasFilters =
    !!activeQuery || !!search.source || search.projectIds.length > 0;

  const clearFilters = useCallback(() => {
    setSearchInput('');
    // Keep the ref in step so the re-sync effect treats this as our own commit.
    committedQuery.current = undefined;
    updateSearch({ q: undefined, source: undefined, projectIds: [] });
  }, [updateSearch]);

  const isEmptyRoster =
    !isPending && !isError && totalResults === 0 && !hasFilters;

  // The team's plan doesn't carry the capability: show an in-app upgrade prompt
  // rather than bouncing to the legacy dashboard. The CTA points at the legacy
  // billing/plans page, which lives on the website origin.
  if (!hasParticipantDatabase) {
    const upgradeHref =
      activeDashboard.organization && teamId
        ? `${env.WEBSITE_URL}/dashboard/organizations/${activeDashboard.organization}/teams/${teamId}/billing/plans`
        : undefined;

    return (
      <div className="flex h-screen flex-col print:block print:h-auto">
        <ResearcherShell user={user} activeDashboard={activeDashboard}>
          {/* Same frame as the loaded roster — heading top-left, one card filling
            the content area — so the gated view doesn't read as a different
            page. */}
          <div className="flex h-full flex-col px-6 py-8">
            <div className="mx-auto flex w-full max-w-360 flex-1 flex-col">
              <div className="flex items-center justify-between">
                <h1 className="font-heading text-display-xs font-semibold text-primary">
                  {t`Participant database`}
                </h1>
              </div>

              <div className="mt-6 flex min-h-0 flex-1 flex-col">
                <div className="flex min-h-96 flex-1 items-center justify-center rounded-xl bg-primary px-6 py-16 shadow-xs ring-1 ring-secondary">
                  <EmptyState size="sm">
                    <EmptyState.Header pattern="dot" patternSize="lg">
                      <Users01 className="relative z-10 size-6 text-fg-secondary" />
                    </EmptyState.Header>
                    <EmptyState.Content>
                      <EmptyState.Title className="text-lg">
                        <Trans>
                          Upgrade to access the participant database
                        </Trans>
                      </EmptyState.Title>
                      <EmptyState.Description>
                        <Trans>
                          Browse everyone your team has paid before and
                          re-invite them to a live project, instead of
                          recruiting from scratch.
                        </Trans>
                      </EmptyState.Description>
                    </EmptyState.Content>
                    {upgradeHref && (
                      <ExternalLinkButton
                        href={upgradeHref}
                        target="_self"
                        color="primary"
                        size="md"
                      >
                        <Trans>Upgrade plan</Trans>
                      </ExternalLinkButton>
                    )}
                  </EmptyState>
                </div>
              </div>
            </div>
          </div>
        </ResearcherShell>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col print:block print:h-auto">
      <ResearcherShell user={user} activeDashboard={activeDashboard}>
        <div className="flex h-full flex-col px-6 py-8">
          <div className="mx-auto flex w-full max-w-360 flex-1 flex-col">
            <div className="flex items-center justify-between">
              <h1 className="font-heading text-display-xs font-semibold text-primary">
                {t`Participant database`}
              </h1>
            </div>

            <div className="mt-6 flex min-h-0 flex-1 flex-col">
              <TableCard.Root className="flex min-h-0 flex-1 flex-col">
                <TableCard.Header
                  title={t`Past participants`}
                  badge={totalResults > 0 ? String(totalResults) : undefined}
                  // Nothing to search, filter or sort against an empty roster —
                  // the controls would just be dead weight above the empty state.
                  contentTrailing={
                    isEmptyRoster ? undefined : (
                      <ParticipantsToolbar
                        searchValue={searchInput}
                        onSearchChange={setSearchInput}
                        source={search.source}
                        onSourceChange={(source) => updateSearch({ source })}
                        projectIds={search.projectIds}
                        onProjectIdsChange={(projectIds) =>
                          updateSearch({ projectIds })
                        }
                        projectOptions={filterProjectOptions}
                        sortBy={search.sortBy}
                        onSortChange={(sortBy) => updateSearch({ sortBy })}
                      />
                    )
                  }
                />

                {/* Bulk-action bar */}
                {selectedIds.size > 0 && (
                  <div className="flex items-center justify-between gap-3 border-b border-secondary bg-secondary px-6 py-3">
                    <span className="text-sm font-medium text-secondary">
                      {selectedIds.size >= MAX_SELECTION
                        ? t`${MAX_SELECTION} selected (maximum)`
                        : t`${selectedIds.size} selected`}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        color="tertiary"
                        size="sm"
                        onPress={clearSelection}
                      >
                        <Trans>Clear</Trans>
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        isDisabled={inviteProjectOptions.length === 0}
                        onPress={openInvite}
                      >
                        <Trans>Invite to project</Trans>
                      </Button>
                    </div>
                  </div>
                )}

                {isError ? (
                  <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
                    <p className="text-sm text-tertiary">
                      <Trans>Could not load participants.</Trans>
                    </p>
                    <Button
                      color="link-color"
                      size="sm"
                      className="mt-2"
                      onPress={() => refetch()}
                    >
                      <Trans>Try again</Trans>
                    </Button>
                  </div>
                ) : isEmptyRoster ? (
                  <div className="flex min-h-96 flex-1 items-center justify-center py-16">
                    <EmptyState size="sm">
                      <EmptyState.Header pattern="dot" patternSize="lg">
                        <Users01 className="relative z-10 size-6 text-fg-secondary" />
                      </EmptyState.Header>
                      <EmptyState.Content>
                        <EmptyState.Title className="text-lg">
                          <Trans>No past participants yet</Trans>
                        </EmptyState.Title>
                        <EmptyState.Description>
                          <Trans>
                            Once you pay someone for a session, they show up
                            here — ready to re-invite to your next project
                            without recruiting them again.
                          </Trans>
                        </EmptyState.Description>
                      </EmptyState.Content>
                      <LinkButton
                        to="/researchers/projects"
                        color="secondary"
                        size="md"
                      >
                        <Trans>Go to projects</Trans>
                      </LinkButton>
                    </EmptyState>
                  </div>
                ) : (
                  <div
                    className={`flex min-h-0 flex-1 flex-col${
                      isPlaceholderData ? ' opacity-60' : ''
                    }`}
                    aria-busy={isPending || isPlaceholderData}
                  >
                    <div className="min-h-0 flex-1 overflow-auto">
                      <Table
                        aria-label={t`Past participants`}
                        striped
                        headerBackground="primary"
                        selectionMode="none"
                        // Skeleton row ids are not profile ids.
                        onRowAction={(key) => {
                          if (!isPending) setSelected(String(key));
                        }}
                      >
                        <Table.Header>
                          <Table.Head
                            id="select"
                            className="w-11 pr-0 pl-4 md:pl-6"
                            label=""
                          >
                            {/* The column header is react-aria "pressable"; stop
                              the pointer press so it doesn't swallow clicks
                              meant for the select-all checkbox. */}
                            <span
                              className="flex"
                              onPointerDown={(event) => event.stopPropagation()}
                            >
                              <Checkbox
                                slot={null}
                                aria-label={
                                  allVisibleSelected
                                    ? t`Deselect all participants`
                                    : t`Select all participants`
                                }
                                size="md"
                                isDisabled={isPending}
                                isSelected={allVisibleSelected}
                                isIndeterminate={someVisibleSelected}
                                onChange={toggleSelectAll}
                              />
                            </span>
                          </Table.Head>
                          <Table.Head
                            id="participant"
                            label={t`Participant`}
                            isRowHeader
                            className="w-full max-w-0 min-w-64"
                          />
                          <Table.Head id="social" label={t`Social`} />
                          <Table.Head id="source" label={t`Source`} />
                          <Table.Head
                            id="lastPaid"
                            label={t`Last participated`}
                          />
                        </Table.Header>
                        <Table.Body
                          renderEmptyState={() => (
                            <EmptyState size="sm" className="py-16">
                              <EmptyState.Header pattern="dot" patternSize="lg">
                                <SearchLg className="relative z-10 size-6 text-fg-secondary" />
                              </EmptyState.Header>
                              <EmptyState.Content>
                                <EmptyState.Title className="text-lg">
                                  <Trans>No matching participants</Trans>
                                </EmptyState.Title>
                                <EmptyState.Description>
                                  <Trans>
                                    No one in your database matches these
                                    filters. Try widening them.
                                  </Trans>
                                </EmptyState.Description>
                              </EmptyState.Content>
                              <Button
                                color="secondary"
                                size="md"
                                onPress={clearFilters}
                              >
                                <Trans>Clear filters</Trans>
                              </Button>
                            </EmptyState>
                          )}
                        >
                          {/* Skeleton rows on the first load — without them the
                            table would render its "no match" empty state while
                            the roster is still on the wire. */}
                          {isPending
                            ? SKELETON_ROWS.map((id) => (
                                <ParticipantDatabaseRowSkeleton
                                  key={id}
                                  id={id}
                                />
                              ))
                            : participants.map((participant) => (
                                <ParticipantDatabaseRow
                                  key={participant.profileId}
                                  participant={participant}
                                  isActive={
                                    participant.profileId ===
                                    selectedParticipant
                                  }
                                  isSelected={selectedIds.has(
                                    participant.profileId,
                                  )}
                                  isSelectable={canSelectMore}
                                  onToggleSelected={toggleSelected}
                                />
                              ))}
                        </Table.Body>
                      </Table>
                    </div>
                    {totalPages > 1 && (
                      <div className="border-t border-secondary px-4 py-3">
                        <PaginationPageDefault
                          page={search.page}
                          total={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    )}
                  </div>
                )}
              </TableCard.Root>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {selectedParticipant && (
            <ParticipantDatabaseDetailPane
              key="participant-database-detail-pane"
              teamId={teamId}
              profileId={selectedParticipant}
              authUserId={user.id}
              onClose={() => setSelected(null)}
            />
          )}
        </AnimatePresence>

        <InviteParticipantsModal
          isOpen={isInviteOpen}
          onOpenChange={setIsInviteOpen}
          teamId={teamId}
          authUserId={user.id}
          participants={[...selectedIds].map((profileId) => ({ profileId }))}
          projectOptions={inviteProjectOptions}
          onInvited={() => {
            clearSelection();
            setIsInviteOpen(false);
          }}
        />
      </ResearcherShell>
    </div>
  );
}
